import type { TilesByLocation } from "@/cache/SpatioTemporalTileCache/PotentiallyVisibleTileSet";
import { instantiateTiles } from "@/cache/SpatioTemporalTileCache/TileArea";
import { TileGeometry } from "@/cache/SpatioTemporalTileCache/TileGeometry";
import type { MapViewport } from "@/layers/MapViewport";
import shaderSources from "@/layers/shaders/all";

type TilesByZoomLevel = Map<number, TilesByLocation<any>>;

export type BinaryPrepassTargets = {
  baseColorTexture?: WebGLTexture;
  overlayColorTexture?: WebGLTexture;
  baseFramebuffer?: WebGLFramebuffer;
  overlayFramebuffer?: WebGLFramebuffer;
  isFloat: boolean;
};

export type BinaryPrepassEnsureResult = {
  ok: boolean;
  reason?: string;
};

export class BinaryTilePrepassRenderer {
  private program?: WebGLProgram;
  private aPos = -1;
  private aUv = -1;
  private uMatrix?: WebGLUniformLocation;
  private uSampler?: WebGLUniformLocation;
  private uOpacity?: WebGLUniformLocation;

  private basePrepassFbo?: WebGLFramebuffer;
  private overlayPrepassFbo?: WebGLFramebuffer;
  private basePrepassColorTex?: WebGLTexture;
  private overlayPrepassColorTex?: WebGLTexture;
  private prepassWidth = 0;
  private prepassHeight = 0;
  private prepassIsFloat = false;

  private quadVertexBuffer?: WebGLBuffer;
  private texCoordBuffer?: WebGLBuffer;
  private quadVertexData?: Float32Array;
  private texCoordData?: Float32Array;
  private texCoordsAreFull = true;

  init(gl: WebGL2RenderingContext): void {
    const hasFloatSupport = !!gl.getExtension("EXT_color_buffer_float");
    if (!hasFloatSupport) {
      return;
    }

    const vsSource = shaderSources("./binary/prepass.vert").default;
    const fsSource = shaderSources("./binary/prepass.frag").default;

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) {
        throw new Error("Failed to create shader");
      }
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(sh) || "";
        gl.deleteShader(sh);
        throw new Error(log);
      }
      return sh;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSource);
    const fs = compile(gl.FRAGMENT_SHADER, fsSource);

    const program = gl.createProgram();
    if (!program) {
      throw new Error("Failed to create program");
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);

    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program) || "";
      gl.deleteProgram(program);
      throw new Error(log);
    }

    this.program = program;
    this.aPos = gl.getAttribLocation(program, "a_position");
    this.aUv = gl.getAttribLocation(program, "a_texCoord");
    this.uMatrix = gl.getUniformLocation(program, "u_mercatorToGl") || undefined;
    this.uSampler = gl.getUniformLocation(program, "u_tileSampler") || undefined;
    this.uOpacity = gl.getUniformLocation(program, "u_opacity") || undefined;

    this.texCoordData = new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);
    this.texCoordsAreFull = true;
    this.quadVertexData = new Float32Array(18);

    this.texCoordBuffer = gl.createBuffer() || undefined;
    if (this.texCoordBuffer && this.texCoordData) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.texCoordData, gl.DYNAMIC_DRAW);
    }

    this.quadVertexBuffer = gl.createBuffer() || undefined;
    if (this.quadVertexBuffer && this.quadVertexData) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVertexBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.quadVertexData.byteLength, gl.DYNAMIC_DRAW);
    }
  }

  dispose(gl: WebGL2RenderingContext): void {
    this.deleteTargets(gl);
    if (this.program) {
      gl.deleteProgram(this.program);
    }
    if (this.quadVertexBuffer) {
      gl.deleteBuffer(this.quadVertexBuffer);
    }
    if (this.texCoordBuffer) {
      gl.deleteBuffer(this.texCoordBuffer);
    }
    this.program = undefined;
    this.quadVertexBuffer = undefined;
    this.texCoordBuffer = undefined;
    this.quadVertexData = undefined;
    this.texCoordData = undefined;
    this.prepassWidth = 0;
    this.prepassHeight = 0;
  }

  getTargets(): BinaryPrepassTargets {
    return {
      baseColorTexture: this.basePrepassColorTex,
      overlayColorTexture: this.overlayPrepassColorTex,
      baseFramebuffer: this.basePrepassFbo,
      overlayFramebuffer: this.overlayPrepassFbo,
      isFloat: this.prepassIsFloat,
    };
  }

  ensureTargets(gl: WebGL2RenderingContext): BinaryPrepassEnsureResult {
    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;

    if (
      this.basePrepassFbo &&
      this.overlayPrepassFbo &&
      this.basePrepassColorTex &&
      this.overlayPrepassColorTex &&
      this.prepassWidth === w &&
      this.prepassHeight === h
    ) {
      return { ok: true };
    }

    this.prepassWidth = w;
    this.prepassHeight = h;
    this.deleteTargets(gl);

    const floatTargets = this.createTargetPair(gl, w, h, true);
    if (floatTargets.ok) {
      this.prepassIsFloat = true;
      this.basePrepassColorTex = floatTargets.baseTexture;
      this.overlayPrepassColorTex = floatTargets.overlayTexture;
      this.basePrepassFbo = floatTargets.baseFramebuffer;
      this.overlayPrepassFbo = floatTargets.overlayFramebuffer;
      return { ok: true };
    }

    const rgbaTargets = this.createTargetPair(gl, w, h, false);
    if (!rgbaTargets.ok) {
      return { ok: false, reason: rgbaTargets.reason || floatTargets.reason || "prepass framebuffer incomplete" };
    }

    this.prepassIsFloat = false;
    this.basePrepassColorTex = rgbaTargets.baseTexture;
    this.overlayPrepassColorTex = rgbaTargets.overlayTexture;
    this.basePrepassFbo = rgbaTargets.baseFramebuffer;
    this.overlayPrepassFbo = rgbaTargets.overlayFramebuffer;
    return { ok: true };
  }

  draw(
    gl: WebGL2RenderingContext,
    matrix: number[],
    viewport: MapViewport,
    baseTilesByZoomLevel: TilesByZoomLevel | undefined,
    overlayTilesByZoomLevel: TilesByZoomLevel | undefined,
    overlayTileOpacityByKey: Map<string, number> | undefined,
    tileService: { networkCache?: { emptyTile?: unknown; failureTile?: unknown } } | undefined,
  ): void {
    if (!this.basePrepassFbo || !this.overlayPrepassFbo || !this.basePrepassColorTex || !this.overlayPrepassColorTex) {
      return;
    }
    if (!this.program || !this.quadVertexBuffer || !this.texCoordBuffer) {
      return;
    }

    this.drawLayer(gl, this.basePrepassFbo, matrix, viewport, baseTilesByZoomLevel, undefined, tileService);
    this.drawLayer(
      gl,
      this.overlayPrepassFbo,
      matrix,
      viewport,
      overlayTilesByZoomLevel,
      overlayTileOpacityByKey,
      tileService,
    );
  }

  private drawLayer(
    gl: WebGL2RenderingContext,
    framebuffer: WebGLFramebuffer,
    matrix: number[],
    viewport: MapViewport,
    tilesByZoomLevel: TilesByZoomLevel | undefined,
    opacityByTileKey: Map<string, number> | undefined,
    tileService: { networkCache?: { emptyTile?: unknown; failureTile?: unknown } } | undefined,
  ) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, this.prepassWidth, this.prepassHeight);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    if (!tilesByZoomLevel || tilesByZoomLevel.size === 0) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return;
    }

    const program = this.program;
    if (!program) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return;
    }

    gl.useProgram(program);
    if (this.uMatrix) {
      gl.uniformMatrix4fv(this.uMatrix, false, matrix as any);
    }

    gl.activeTexture(gl.TEXTURE0);
    if (this.uSampler) {
      gl.uniform1i(this.uSampler, 0);
    }

    const emptyTile = tileService?.networkCache?.emptyTile;
    const failureTile = tileService?.networkCache?.failureTile;
    const ascendingLayers = [...tilesByZoomLevel.entries()].sort((a, b) => a[0] - b[0]);
    const targetTileZoom = Math.floor(viewport.zoom);

    for (const [, sublayerTiles] of ascendingLayers) {
      for (const [tileKey, { tileData, tileGeometry, isFailedTile }] of Object.entries(sublayerTiles) as any) {
        if (isFailedTile || !tileData || !tileGeometry) {
          continue;
        }
        if (tileData === emptyTile || tileData === failureTile) {
          continue;
        }

        const tileOpacity = opacityByTileKey?.get(tileKey) ?? 1;
        if (tileOpacity <= 0) {
          continue;
        }
        if (this.uOpacity) {
          gl.uniform1f(this.uOpacity, tileOpacity);
        }

        gl.bindTexture(gl.TEXTURE_2D, tileData.texture(gl));

        const tileZoom = tileGeometry.z;
        const needsScalingUp = tileZoom < targetTileZoom;
        const needsClippingDown = tileZoom > targetTileZoom;

        if (needsScalingUp) {
          this.renderScaledTile(gl, tileGeometry, targetTileZoom, viewport);
        } else if (needsClippingDown) {
          this.renderClippedTile(gl, tileGeometry, targetTileZoom, viewport);
        } else {
          this.renderNormalTile(gl, tileGeometry, viewport);
        }
      }
    }

    gl.disableVertexAttribArray(this.aPos);
    gl.disableVertexAttribArray(this.aUv);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private createTargetPair(gl: WebGL2RenderingContext, w: number, h: number, useFloat: boolean) {
    const baseTexture = gl.createTexture();
    const overlayTexture = gl.createTexture();
    const baseFramebuffer = gl.createFramebuffer();
    const overlayFramebuffer = gl.createFramebuffer();
    if (!baseTexture || !overlayTexture || !baseFramebuffer || !overlayFramebuffer) {
      this.deleteAllocatedPair(gl, baseTexture, overlayTexture, baseFramebuffer, overlayFramebuffer);
      return { ok: false, reason: "failed to allocate prepass targets" as const };
    }

    const configureTexture = (texture: WebGLTexture) => {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      if (useFloat) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, w, h, 0, gl.RGBA, gl.FLOAT, null);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      }
    };

    configureTexture(baseTexture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, baseFramebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, baseTexture, 0);
    const baseStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);

    configureTexture(overlayTexture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, overlayFramebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, overlayTexture, 0);
    const overlayStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);

    if (baseStatus !== gl.FRAMEBUFFER_COMPLETE || overlayStatus !== gl.FRAMEBUFFER_COMPLETE) {
      this.deleteAllocatedPair(gl, baseTexture, overlayTexture, baseFramebuffer, overlayFramebuffer);
      return { ok: false, reason: `prepass framebuffer incomplete (${useFloat ? "float" : "rgba8"})` as const };
    }

    return {
      ok: true as const,
      baseTexture,
      overlayTexture,
      baseFramebuffer,
      overlayFramebuffer,
    };
  }

  private deleteAllocatedPair(
    gl: WebGL2RenderingContext,
    baseTexture?: WebGLTexture | null,
    overlayTexture?: WebGLTexture | null,
    baseFramebuffer?: WebGLFramebuffer | null,
    overlayFramebuffer?: WebGLFramebuffer | null,
  ) {
    if (baseTexture) {
      gl.deleteTexture(baseTexture);
    }
    if (overlayTexture) {
      gl.deleteTexture(overlayTexture);
    }
    if (baseFramebuffer) {
      gl.deleteFramebuffer(baseFramebuffer);
    }
    if (overlayFramebuffer) {
      gl.deleteFramebuffer(overlayFramebuffer);
    }
  }

  private deleteTargets(gl: WebGL2RenderingContext) {
    this.deleteAllocatedPair(
      gl,
      this.basePrepassColorTex,
      this.overlayPrepassColorTex,
      this.basePrepassFbo,
      this.overlayPrepassFbo,
    );
    this.basePrepassColorTex = undefined;
    this.overlayPrepassColorTex = undefined;
    this.basePrepassFbo = undefined;
    this.overlayPrepassFbo = undefined;
  }

  private renderNormalTile(gl: WebGL2RenderingContext, tileGeometry: TileGeometry, viewport: MapViewport): void {
    const instances = [...instantiateTiles(viewport.viewportArea, tileGeometry)];
    for (const instance of instances) {
      const [n, w, s, e] = instance.mercator(0);
      this.renderTileInstance(gl, w, s, e, n, FULL_TEXTURE_COORDS, 0.0);
    }
  }

  private renderScaledTile(
    gl: WebGL2RenderingContext,
    tileGeometry: TileGeometry,
    targetTileZoom: number,
    viewport: MapViewport,
  ): void {
    const zoomDiff = targetTileZoom - tileGeometry.z;
    if (zoomDiff <= 0) {
      return;
    }
    const tilesPerSide = 1 << zoomDiff;
    const visibleRange = this.getVisibleScaledTileRange(tileGeometry, targetTileZoom, viewport);
    if (!visibleRange) {
      return;
    }
    const unbiasedViewportArea = this.getInstantiatedViewportArea(viewport);

    for (let dy = visibleRange.minDy; dy <= visibleRange.maxDy; dy++) {
      for (let dx = visibleRange.minDx; dx <= visibleRange.maxDx; dx++) {
        const virtualX = tileGeometry.x * tilesPerSide + dx;
        const virtualY = tileGeometry.y * tilesPerSide + dy;

        const virtualTileGeometry = new TileGeometry([virtualX, virtualY, targetTileZoom]);
        const instances = [...instantiateTiles(unbiasedViewportArea, virtualTileGeometry)];
        const texCoords = calculateTextureCoordinates(dx, dy, tilesPerSide);

        for (const instance of instances) {
          const [n, w, s, e] = instance.mercator(0);
          this.renderTileInstance(gl, w, s, e, n, texCoords, 0.0);
        }
      }
    }
  }

  private renderClippedTile(
    gl: WebGL2RenderingContext,
    tileGeometry: TileGeometry,
    targetTileZoom: number,
    viewport: MapViewport,
  ): void {
    const zoomDiff = tileGeometry.z - targetTileZoom;
    if (zoomDiff <= 0) {
      return;
    }

    const unbiasedViewportArea = this.getInstantiatedViewportArea(viewport);
    const scaleFactor = 1 << zoomDiff;
    const ancestorX = Math.floor(tileGeometry.x / scaleFactor);
    const ancestorY = Math.floor(tileGeometry.y / scaleFactor);
    const ancestorGeom = new TileGeometry([ancestorX, ancestorY, targetTileZoom]);
    const ancestorInstances = [...instantiateTiles(unbiasedViewportArea, ancestorGeom)];
    if (ancestorInstances.length === 0) {
      return;
    }

    const childIndexX = tileGeometry.x % scaleFactor;
    const childIndexY = tileGeometry.y % scaleFactor;

    for (const instance of ancestorInstances) {
      const [ancestorN, ancestorW, ancestorS, ancestorE] = instance.mercator(0);
      const ancestorWidth = ancestorE - ancestorW;
      const ancestorHeight = ancestorN - ancestorS;
      const childWidth = ancestorWidth / scaleFactor;
      const childHeight = ancestorHeight / scaleFactor;
      const childW = ancestorW + childIndexX * childWidth;
      const childE = childW + childWidth;
      const childN = ancestorN - childIndexY * childHeight;
      const childS = childN - childHeight;

      this.renderTileInstance(gl, childW, childS, childE, childN, FULL_TEXTURE_COORDS, 0.0);
    }
  }

  private renderTileInstance(
    gl: WebGL2RenderingContext,
    w: number,
    s: number,
    e: number,
    n: number,
    texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number },
    depth: number,
  ): void {
    this.updateTextureCoordinateBuffer(gl, texCoords);
    this.updateVertexBuffer(gl, w, s, e, n, depth);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  private updateTextureCoordinateBuffer(
    gl: WebGL2RenderingContext,
    texCoords: { texLeft: number; texRight: number; texTop: number; texBottom: number },
  ): void {
    if (!this.texCoordBuffer || !this.texCoordData) {
      return;
    }
    let needsUpload = true;
    if (texCoords === FULL_TEXTURE_COORDS) {
      needsUpload = !this.texCoordsAreFull;
      if (needsUpload) {
        this.texCoordData[0] = 0;
        this.texCoordData[1] = 0;
        this.texCoordData[2] = 1;
        this.texCoordData[3] = 0;
        this.texCoordData[4] = 0;
        this.texCoordData[5] = 1;
        this.texCoordData[6] = 0;
        this.texCoordData[7] = 1;
        this.texCoordData[8] = 1;
        this.texCoordData[9] = 0;
        this.texCoordData[10] = 1;
        this.texCoordData[11] = 1;
        this.texCoordsAreFull = true;
      }
    } else {
      this.texCoordsAreFull = false;
      this.texCoordData[0] = texCoords.texLeft;
      this.texCoordData[1] = texCoords.texBottom;
      this.texCoordData[2] = texCoords.texRight;
      this.texCoordData[3] = texCoords.texBottom;
      this.texCoordData[4] = texCoords.texLeft;
      this.texCoordData[5] = texCoords.texTop;
      this.texCoordData[6] = texCoords.texLeft;
      this.texCoordData[7] = texCoords.texTop;
      this.texCoordData[8] = texCoords.texRight;
      this.texCoordData[9] = texCoords.texBottom;
      this.texCoordData[10] = texCoords.texRight;
      this.texCoordData[11] = texCoords.texTop;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    if (needsUpload) {
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.texCoordData);
    }
    gl.enableVertexAttribArray(this.aUv);
    gl.vertexAttribPointer(this.aUv, 2, gl.FLOAT, false, 0, 0);
  }

  private updateVertexBuffer(
    gl: WebGL2RenderingContext,
    w: number,
    s: number,
    e: number,
    n: number,
    depth: number,
  ): void {
    if (!this.quadVertexBuffer || !this.quadVertexData) {
      return;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.quadVertexData[0] = w;
    this.quadVertexData[1] = s;
    this.quadVertexData[2] = depth;
    this.quadVertexData[3] = e;
    this.quadVertexData[4] = s;
    this.quadVertexData[5] = depth;
    this.quadVertexData[6] = w;
    this.quadVertexData[7] = n;
    this.quadVertexData[8] = depth;
    this.quadVertexData[9] = w;
    this.quadVertexData[10] = n;
    this.quadVertexData[11] = depth;
    this.quadVertexData[12] = e;
    this.quadVertexData[13] = s;
    this.quadVertexData[14] = depth;
    this.quadVertexData[15] = e;
    this.quadVertexData[16] = n;
    this.quadVertexData[17] = depth;
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.quadVertexData);
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 3, gl.FLOAT, false, 0, 0);
  }

  private getVisibleScaledTileRange(
    tileGeometry: TileGeometry,
    targetTileZoom: number,
    viewport: MapViewport,
  ): { minDx: number; maxDx: number; minDy: number; maxDy: number } | null {
    const scaleFactor = 2 ** (targetTileZoom - tileGeometry.z);
    const tileMinX = tileGeometry.x * scaleFactor;
    const tileMaxX = tileMinX + scaleFactor - 1;
    const tileMinY = tileGeometry.y * scaleFactor;
    const tileMaxY = tileMinY + scaleFactor - 1;

    const visibleMinX = Math.max(tileMinX, Math.floor(viewport.viewportArea.xyMin[0]));
    const visibleMaxX = Math.min(tileMaxX, Math.floor(viewport.viewportArea.xyMax[0]));
    const visibleMinY = Math.max(tileMinY, Math.floor(viewport.viewportArea.xyMin[1]));
    const visibleMaxY = Math.min(tileMaxY, Math.floor(viewport.viewportArea.xyMax[1]));

    if (visibleMinX > visibleMaxX || visibleMinY > visibleMaxY) {
      return null;
    }

    return {
      minDx: visibleMinX - tileMinX,
      maxDx: visibleMaxX - tileMinX,
      minDy: visibleMinY - tileMinY,
      maxDy: visibleMaxY - tileMinY,
    };
  }

  private getInstantiatedViewportArea(viewport: MapViewport): MapViewport["viewportArea"] {
    return {
      ...viewport.viewportArea,
      xyMin: [Math.floor(viewport.viewportArea.xyMin[0]), Math.floor(viewport.viewportArea.xyMin[1])],
      xyMax: [Math.floor(viewport.viewportArea.xyMax[0]), Math.floor(viewport.viewportArea.xyMax[1])],
      zLodBias: 0,
    };
  }
}

export const FULL_TEXTURE_COORDS = { texLeft: 0, texRight: 1, texTop: 1, texBottom: 0 };

export function calculateTextureCoordinates(dx: number, dy: number, tilesPerSide: number) {
  const texTop = 1 - dy / tilesPerSide;
  const texBottom = 1 - (dy + 1) / tilesPerSide;
  return {
    texLeft: dx / tilesPerSide,
    texRight: (dx + 1) / tilesPerSide,
    texTop,
    texBottom,
  };
}
